import {
    Component,
    inject,
    signal,
    HostListener,
    ViewChild,
    OnInit,
    OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { Menu, MenuModule } from 'primeng/menu';
import { Drawer } from 'primeng/drawer';
import { BadgeModule } from 'primeng/badge';
import { MenuItem } from 'primeng/api';
import { ThemeService } from '../../core/services/theme.service';
import { AuthService } from '../../services/auth.service';
import { NotificationsApiService } from '../../services/notifications-api.service';
import { AdminNotification } from '../../interfaces/notification.interface';

interface NavGroup {
    label: string;
    icon: string;
    children?: NavChild[];
    route?: string;
}

interface NavChild {
    label: string;
    icon: string;
    route: string;
}

@Component({
    selector: 'app-header',
    standalone: true,
    imports: [CommonModule, RouterModule, ButtonModule, TooltipModule, MenuModule, Drawer, BadgeModule],
    templateUrl: './header.component.html',
    styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy {
    @ViewChild('userMenu') userMenu!: Menu;

    readonly themeService = inject(ThemeService);
    private readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    readonly notifications = inject(NotificationsApiService);

    mobileMenuOpen = signal(false);
    activeDropdown = signal<string | null>(null);
    notifDropdownOpen = signal(false);

    get notifCount() { return this.notifications.unreadCount; }

    ngOnInit(): void {
        this.notifications.startPolling();
    }

    ngOnDestroy(): void {
        this.notifications.stopPolling();
    }

    toggleNotifDropdown(event: MouseEvent) {
        event.stopPropagation();
        this.notifDropdownOpen.update(v => !v);
    }

    closeNotifDropdown() {
        this.notifDropdownOpen.set(false);
    }

    async markAllNotificationsRead(event?: Event) {
        event?.stopPropagation();
        await this.notifications.markAllRead();
    }

    async onNotifClick(n: AdminNotification) {
        if (!n.is_read) await this.notifications.markRead(n._id);
        this.closeNotifDropdown();
        if (n.link_url) this.router.navigateByUrl(n.link_url);
    }

    iconForType(type: string): string {
        switch (type) {
            case 'new_order': return 'pi pi-shopping-bag';
            case 'order_cancelled': return 'pi pi-times-circle';
            case 'contact_enquiry': return 'pi pi-envelope';
            default: return 'pi pi-bell';
        }
    }

    get user() { return this.authService.user; }

    get initials(): string {
        return (this.user?.name ?? 'A')
            .split(' ')
            .filter(Boolean)
            .slice(0, 2)
            .map(w => w[0].toUpperCase())
            .join('');
    }

    readonly navGroups: NavGroup[] = [
        { label: 'Dashboard', icon: 'pi pi-chart-line', route: '/dashboard' },
        {
            label: 'Master', icon: 'pi pi-database',
            children: [
                { label: 'Slider', icon: 'pi pi-images', route: '/slider' },
                { label: 'Website Home', icon: 'pi pi-home', route: '/website-home' },
                { label: 'Order Statuses', icon: 'pi pi-tags', route: '/order-statuses' },
                { label: 'Coupons', icon: 'pi pi-ticket', route: '/coupons' },
            ],
        },
        {
            label: 'Catalog', icon: 'fa-solid fa-tshirt',
            children: [
                { label: 'Products', icon: 'pi pi-box', route: '/products' },
                { label: 'Brands', icon: 'pi pi-tag', route: '/brands' },
                { label: 'Categories', icon: 'pi pi-sitemap', route: '/categories' },
            ],
        },
        {
            label: 'Orders', icon: 'pi pi-shopping-cart',
            children: [
                { label: 'Orders', icon: 'pi pi-list', route: '/orders' },
                { label: 'Payments', icon: 'pi pi-credit-card', route: '/payments' },
                { label: 'Shipping Rules', icon: 'pi pi-truck', route: '/shipping-rules' },
            ],
        },
        { label: 'Customers', icon: 'pi pi-users', route: '/customers' },
        { label: 'Reports', icon: 'pi pi-chart-bar', route: '/reports' },
        {
            label: 'Content', icon: 'pi pi-file',
            children: [
                { label: 'Reviews', icon: 'pi pi-star', route: '/reviews' },
                { label: 'FAQs', icon: 'pi pi-question-circle', route: '/faqs' },
                { label: 'Contact Enquiries', icon: 'pi pi-envelope', route: '/contact-enquiries' },
                { label: 'Static Pages', icon: 'pi pi-file', route: '/static-pages' },
                { label: 'Brands Page', icon: 'pi pi-tags', route: '/brands-page' },
                { label: 'About Page', icon: 'pi pi-info-circle', route: '/about-page' },
            ],
        },
        { label: 'Admin Users', icon: 'pi pi-users', route: '/admin-users' },
        { label: 'Settings', icon: 'pi pi-cog', route: '/settings' },
    ];

    readonly userMenuItems: MenuItem[] = [
        {
            label: 'Account',
            items: [
                { label: 'My Profile', icon: 'pi pi-user', command: () => this.router.navigate(['/admin-users']) },
                { separator: true },
                { label: 'Logout', icon: 'pi pi-sign-out', command: () => this.authService.logout() },
            ],
        },
    ];

    toggleDropdown(label: string): void {
        this.activeDropdown.set(this.activeDropdown() === label ? null : label);
    }

    navigate(route: string): void {
        this.router.navigate([route]);
        this.activeDropdown.set(null);
        this.mobileMenuOpen.set(false);
    }

    toggleUserMenu(event: Event): void {
        this.userMenu.toggle(event);
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.desktop-nav') && !target.closest('.dropdown-menu')) {
            this.activeDropdown.set(null);
        }
        if (!target.closest('.notif-wrap')) {
            this.notifDropdownOpen.set(false);
        }
    }

    mobileOpenGroup = signal<string | null>(null);
    toggleMobileGroup(label: string): void {
        this.mobileOpenGroup.set(this.mobileOpenGroup() === label ? null : label);
    }
}
